import type { Concept } from '@yuan-math/curriculum';

/**
 * Geometry — the high-school course, and the odd one out.
 *
 * Every other band adds technique. This one adds *justification*. A student
 * arriving here can already find an angle, a length and an area; what they
 * cannot yet do is say why their answer must be right rather than why it looks
 * right. That is the whole difference between measuring a hundred triangles and
 * knowing something about all of them.
 *
 * So `geometric-proof` comes first and nearly everything depends on it. The
 * rest of the course is a sequence of results worth proving, chosen because
 * each one is genuinely surprising the first time: that two triangles agreeing
 * in three measurements must agree in all six; that every angle standing on the
 * same arc is the same size; that the ratio of two sides of a right triangle
 * depends on nothing but its angles.
 *
 * That last fact is why `trig-ratios` depends on `similarity` and not only on
 * `pythagoras`. Sine is well defined *because* all right triangles sharing an
 * angle are similar — without that, "the" ratio would not be a number at all.
 */
export const GEOMETRY_HS_CONCEPTS: Concept[] = [
  /* --------------------------------------------------------- foundations */
  {
    id: 'geometric-proof',
    title: 'Reasoning and Proof',
    summary: 'Arguing from what is given to what must follow — the move that separates mathematics from measurement.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['angles', 'algebraic-expressions'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'conditional-statements',
    title: 'Conditional Statements',
    summary: 'If A then B — and the three other statements you can build from it, only one of which comes free.',
    strand: 'discrete',
    levels: ['grade-10'],
    prerequisites: ['geometric-proof'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'parallel-lines',
    title: 'Parallel Lines and Transversals',
    summary: 'One line crossing two parallels makes eight angles, and only two different sizes among them.',
    strand: 'geometry',
    levels: ['grade-10'],
    /*
     * The configuration itself is Grade 8 (`angle-relationships`); what this
     * concept adds is the *argument* — which is why it also needs
     * `geometric-proof`. Naming both keeps the recommendation engine honest: a
     * student failing a transversal question here is sent back to the informal
     * lesson, not told to re-read the definition of a proof.
     */
    prerequisites: ['geometric-proof', 'angle-relationships'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ----------------------------------------------------------- triangles */
  {
    id: 'triangle-congruence',
    title: 'Congruent Triangles',
    summary: 'Three of the six measurements are enough to fix a triangle completely — but only the right three.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['geometric-proof', 'transformations'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'triangle-properties',
    title: 'Properties of Triangles',
    summary: 'What follows from congruence: isosceles triangles, the exterior angle, and which side lengths are possible at all.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['triangle-congruence'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'similarity',
    title: 'Similar Triangles',
    summary: 'Same shape, different size — and the reason a scale drawing tells you anything at all.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['triangle-congruence', 'proportions'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'triangle-proportionality',
    title: 'Proportionality in Triangles',
    summary: 'A line parallel to one side cuts the other two in the same ratio — and the two results that follow from saying so carefully.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['similarity', 'parallel-lines'],
    status: 'available',
    estimatedMinutes: 35,
  },

  {
    id: 'constructions',
    title: 'Constructions with Compass and Straightedge',
    summary: 'Drawing a figure that is exactly right rather than nearly right — and proving it with the congruence you already have.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['triangle-congruence', 'circles'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------------- polygons and circles */
  {
    id: 'polygons',
    title: 'Polygons and Quadrilaterals',
    summary: 'Angle sums for any number of sides, and what makes a parallelogram a parallelogram.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['parallel-lines', 'triangle-properties'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'quadrilateral-properties',
    title: 'Quadrilaterals and Their Diagonals',
    summary: 'What each name in the family actually guarantees, and the diagonals that tell one from another.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['polygons', 'triangle-congruence'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'circle-theorems',
    title: 'Circle Theorems',
    summary: 'Angles, chords and tangents — where several of the most surprising results in elementary geometry live.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['circles', 'geometric-proof'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'chords-secants',
    title: 'Chords, Secants and Tangents',
    summary: 'Lines cutting a circle, and the product of two lengths that turns out not to depend on where they cross.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['circle-theorems', 'similarity'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'arc-sector',
    title: 'Arc Length and Sector Area',
    summary: 'A slice of a circle is a fraction of the whole one, and the fraction is written on the angle.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['circles', 'proportions'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* -------------------------------------------- measurement and position */
  {
    id: 'coordinate-geometry',
    title: 'Coordinate Geometry',
    summary: 'Distance, midpoint and the equation of a circle — geometry done with algebra instead of a compass.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['pythagoras', 'linear-functions'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'special-triangles',
    title: 'Special Right Triangles',
    summary: 'Two triangles whose sides are always in the same ratio, so their lengths need no calculation.',
    strand: 'geometry',
    levels: ['grade-10'],
    prerequisites: ['pythagoras', 'radicals'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'trig-ratios',
    title: 'Right-Triangle Trigonometry',
    summary: 'Sine, cosine and tangent — ratios that depend on an angle and nothing else.',
    strand: 'trigonometry',
    levels: ['grade-10'],
    prerequisites: ['similarity', 'special-triangles'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'trig-applications',
    title: 'Solving Right Triangles',
    summary: 'Running the ratios backwards to find an angle, and pointing the whole apparatus at something too tall to measure.',
    strand: 'trigonometry',
    levels: ['grade-10'],
    prerequisites: ['trig-ratios', 'pythagoras'],
    status: 'available',
    estimatedMinutes: 40,
  },
];
